import Handsontable from 'handsontable'
import processChange from './processChange'
import options from './options'
import ApiService from './apiService'
import CellProcessor from './cellprocessors/cellProcessor'
import loadData from './loadData'
import FunctionExecutor from './functions/functionExecutor'
import * as constants from './constants'
import heightBugFix from './handsontable-fixes/heightBugFix'
import RemoveRow from './plugins/removeRowplugin'

export default function (element) {
    const $target = $(element);
    const optionsFromElement = options($target);
    const apiService = new ApiService(optionsFromElement.api);
    const funcExecutor = new FunctionExecutor(optionsFromElement.table);
    const cellProcessor = new CellProcessor(optionsFromElement.table);
    const colDefinitions = optionsFromElement.table.columns;
    const htRemoveRow = new RemoveRow();

    const hot = new Handsontable(element,
        {
            afterInit: function() {
                htRemoveRow.init(this);
            },
            modifyRowHeight: function(height, row) {
                const fixedRowHeights = optionsFromElement.table.rowHeights;
                if (fixedRowHeights && row < fixedRowHeights.length) {
                    return fixedRowHeights[row];
                }

                return height;
            },
            beforeInitWalkontable: function (walkontableConfig) {
                htRemoveRow.beforeInitWalkontable(this, walkontableConfig);
            },
            beforeChange: function (changes, source) {
                // fix for checkbox
                if (source !== 'CopyPaste.paste') {
                    return;
                }
                changes.forEach(function (change) {
                    const info = new Change(change, colDefinitions);
                    if (info.type === 'checkbox') {
                        if (change[3] === 'false' || change[3] === 'NE' || change[3] === 'NO') {
                            change[3] = false;
                        } else if (change[3] == true) {
                            change[3] = true;
                        } else {
                            change[3] = false;
                        }
                    }

                });
            },
            afterChange: (handsonTableChange, source) => {
                if (shouldIgnore(handsonTableChange, source)) {
                    return;
                }
                const changes = handsonTableChange.map(x => new Change(x, colDefinitions));
                executeExcelFunctions(changes, funcExecutor, hot);

                const rowChanges = processChange(changes, source, (row) => { return getIdForRow(row, hot) });
                const validRowChanges = filterInvalidRows(rowChanges, hot);
                if (validRowChanges && validRowChanges.length > 0) {
                    const toCreate = validRowChanges.filter(x => x.id < 0).map(x => ({
                        row: x.row,
                        data: hot.getSourceDataAtRow(x.row)
                    }));
                    const toUpdate = validRowChanges.filter(x => x.id > 0).map(x => ({
                        id: getIdForRow(x.row, hot), ...hot.getSourceDataAtRow(x.row)
                    }));
                    apiService.create(toCreate, (row, id) => { setIdToRow(row, id, hot) });
                    apiService.update(toUpdate);
                }
                hot.render();
            },
            beforeRemoveRow: (index, amount, visualRows) => {
                // custom header rows are not allowed to remove
                if ((!!optionsFromElement.table.customHeaderRows && index < optionsFromElement.table.customHeaderRows)
                    || optionsFromElement.table.readOnly === true) {
                    return false;
                }
                const id = getIdForRow(index, hot);
                if (id > 0) {
                    apiService.delete(id);
                }
            },
            afterRemoveRow: (index, amount) => {
                funcExecutor.runAll(hot.getData(),
                    (row, col, value) => { setValueAt(row, col, value, hot); },
                    (metadata) => { setMetadataAt(metadata, hot); });
            },
            cells: function (row, col, prop) {
                const cellProperties = {};
                const cell = this;
                cellProcessor.process(row, col, cell, prop, cellProperties);

                return cellProperties;
            },
            afterDestroy: () => {
                $(document).unbind("fullscreenBoxChanged");
                $(document).unbind("handsontable-lock");
            },
            ...optionsFromElement.table
        });

    heightBugFix(hot);

    apiService.load()
        .done(data => {
            const tableData = loadData(data);
            hot.loadData(tableData.data);
            // bug fix because loadData reset cell styles, but it clears metadata! watch out order
            hot.updateSettings({
                cell: optionsFromElement.table.cell
            });
            // it needs to be after update settings, because of reset 
            registerMetadata(tableData.metadata, hot);
            funcExecutor.runAll(hot.getData(), (row, col, value) => {
                setValueAt(row, col, value, hot);
            }, (metadata) => {
                setMetadataAt(metadata, hot);
            });
        });
    addFullscreenBoxListener(hot);
    addLockListener(hot);

    return hot;
}

function addFullscreenBoxListener(hot) {
    let cssHeight;
    $(document).on('fullscreenBoxChanged', (event) => {
        const element = $(event.element).find('.modal-body');
        if (event.open) {
            cssHeight = element.css('height');
            const container = element.parent().parent();
            const newHeight = container.height() - element.parent().children('.modal-header').height();
            element.css('height', newHeight);
            hot.updateSettings({ height: element.height() });
        } else {
            element.height(cssHeight);
            hot.updateSettings({ height: element.height() });
        }
    });
}

function addLockListener(hot) {
    $(document).on('handsontable-lock', (event) => {
        const lock = event.lock;
        hot.updateSettings({
            readOnly: lock,
            removeRowPlugin: !lock
        });
    });
}

function registerMetadata(ids, hot) {
    ids.forEach(x => {
        setIdToRow(x.row, x.id, hot);
    });
}

function setIdToRow(row, id, hot) {
    setMetadataToRow(row, constants.metadataDatabaseId, id, hot);
}

function setMetadataToRow(row, name, value, hot) {
    hot.setCellMeta(row, constants.metadataCellIndex, name, value);
}

function getIdForRow(row, hot) {
    return hot.getCellMeta(row, constants.metadataCellIndex)[constants.metadataDatabaseId] || -1;
}

function setValueAt(row, col, value, hot) {
    if (value === undefined) {
        return;
    }

    hot.setDataAtCell(row, col, value, 'function');
}

function setMetadataAt(metadata, hot) {
    if (metadata === undefined) {
        return;
    }

    for (let item of metadata) {
        const row = item.row;
        const col = item.col;
        const name = item.metadata.name;
        const value = item.metadata.value;
        hot.setCellMeta(row, col, name, value);
    }
}

function executeExcelFunctions(changes, funcExecutor, hot) {
    for (const change of changes) {
        if (!change.isChanged()) {
            continue;
        }

        funcExecutor.run(change.row, change.col, hot.getData(), (row, col, value) => {
            setValueAt(row, col, value, hot);
        }, (metadata) => {
            setMetadataAt(metadata, hot);
        });
    }
}

function shouldIgnore(handsonTableChange, source) {
    return !handsonTableChange ||
        source === constants.functionChangeSource ||
        source === constants.loadDataChangeSource;
}

function filterInvalidRows(changes, hot) {
    return changes.filter(change => hot.getCellMetaAtRow(change.row).map(x => x.valid).every(x => x !== false));
}

/**
 * Change detected by Handsontable.
 */
class Change {
    constructor(change, colDefinitions) {
        this.row = change[0];
        const colIdentification = change[1];
        if (typeof (colIdentification) === "number") {
            this.col = colIdentification;
            this.type = colDefinitions[colIdentification].type;
        } else {
            this.col = this._getColumnIndex(colIdentification, colDefinitions);
            this.property = colIdentification;
            this.type = colDefinitions.find(x => x.data === colIdentification).type;
        }
        this.newValue = change[2];
        this.prevValue = change[3];
    }

    isChanged() {
        return this.prevValue !== this.newValue;
    }

    _getColumnIndex(name, colDefinitions) {
        return colDefinitions.findIndex(x => x.data === name);
    }

    _getColumnType(name, colDefinitions) {
        return colDefinitions.findIndex(x => x.data === name);
    }
}



// WEBPACK FOOTER //
// ./App/components/Handsontable/create.js